import { useState } from "react";
import { Link } from "react-router-dom";
import { QUIZ_QUESTIONS, ELEMENT_PROFILES } from "@/data/quizData";
import { BOOKING_URL } from "@/data/content";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, RotateCw } from "lucide-react";
import QuizEmailCapture from "@/components/QuizEmailCapture";
import { SP_WIDGET_ATTRS } from "@/components/BookingButton";

function computeResult(answers) {
  const counts = { Wood: 0, Fire: 0, Earth: 0, Metal: 0, Water: 0 };
  answers.forEach((e) => { if (counts[e] !== undefined) counts[e]++; });
  let best = "Earth", max = -1;
  Object.entries(counts).forEach(([k, v]) => { if (v > max) { max = v; best = k; } });
  return best;
}

export default function QuizPage() {
  const [step, setStep] = useState(0); // 0..QUIZ_QUESTIONS.length-1, then "result"
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [quizId, setQuizId] = useState(null);
  const total = QUIZ_QUESTIONS.length;

  const pick = async (element) => {
    const next = [...answers, element];
    setAnswers(next);
    if (step + 1 < total) {
      setStep(step + 1);
    } else {
      const r = computeResult(next);
      setResult(r);
      try {
        const { data } = await api.post("/quiz", { answers: next, result_element: r });
        setQuizId(data.id);
      } catch (err) { console.error("Quiz save failed:", err); /* non-blocking */ }
    }
  };

  const back = () => {
    if (step === 0) return;
    setAnswers(answers.slice(0, -1));
    setStep(step - 1);
  };

  const restart = () => {
    setStep(0); setAnswers([]); setResult(null); setQuizId(null);
  };

  if (result) {
    const p = ELEMENT_PROFILES[result];
    return (
      <main className="min-h-[90vh]" data-testid="quiz-result-page">
        <section className="py-20 lg:py-28">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <div className="eyebrow mb-6" data-testid="quiz-result-element">Your constitution · {p.title}</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
              {p.headline}
            </h1>
            <p className="text-lg leading-relaxed max-w-3xl" style={{ color: "var(--sano-ink-2)" }}>
              {p.description}
            </p>

            <div className="mt-12 grid sm:grid-cols-3 gap-px" style={{ background: "var(--sano-border)" }}>
              {[
                { l: "Season", v: p.season },
                { l: "Organ system", v: p.organ },
                { l: "Emotional axis", v: p.emotion },
              ].map((x) => (
                <div key={x.l} className="p-6" style={{ background: "var(--sano-bg)" }}>
                  <div className="eyebrow mb-2">{x.l}</div>
                  <div className="font-serif-display text-2xl">{x.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-16 grid lg:grid-cols-2 gap-12">
              <Pillar title="Foods to lean into" items={p.foods} testid="foods" />
              <Pillar title="Herbs" items={p.herbs} testid="herbs" />
              <Pillar title="Teas" items={p.teas} testid="teas" />
              <div data-testid="acupressure">
                <div className="eyebrow mb-4">Acupressure points</div>
                <ul className="space-y-5">
                  {p.points.map((pt) => (
                    <li key={pt.name}>
                      <div className="font-serif-display text-xl">{pt.name}</div>
                      <div className="text-sm leading-relaxed mt-1" style={{ color: "var(--sano-ink-2)" }}>{pt.how}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div
              className="mt-16 p-10 lg:p-14 text-center border"
              style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
              data-testid="quiz-mantra"
            >
              <div className="eyebrow mb-4">Your mantra</div>
              <div className="font-serif-display text-3xl sm:text-4xl italic leading-snug">
                “{p.mantra}”
              </div>
            </div>

            <QuizEmailCapture quizId={quizId} element={p.title} />

            <div className="mt-16 grid md:grid-cols-2 gap-6">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noreferrer"
                {...SP_WIDGET_ATTRS}
                className="block p-10 transition-all hover:opacity-95"
                style={{ background: "var(--sano-olive)", color: "var(--sano-bg)" }}
                data-testid="quiz-book-cta"
              >
                <div className="eyebrow mb-3" style={{ color: "var(--sano-bg-2)" }}>Next step</div>
                <div className="font-serif-display text-3xl mb-2">Book a session to balance your {p.title}.</div>
                <div className="text-sm opacity-80">A 75-minute nervous-system reset built around your constitution.</div>
                <div className="mt-6 inline-flex items-center gap-2 text-sm border-b border-current pb-0.5">
                  Book at sanoacu.com <ArrowRight size={14} strokeWidth={1.5} />
                </div>
              </a>
              <button
                onClick={restart}
                className="p-10 text-left border hover:bg-[#F0EBE1] transition-all"
                style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}
                data-testid="quiz-restart"
              >
                <div className="eyebrow mb-3">Curious?</div>
                <div className="font-serif-display text-3xl mb-2">Take the quiz again.</div>
                <div className="text-sm" style={{ color: "var(--sano-ink-2)" }}>
                  Your constitution can shift across the seasons of your life.
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-sm">
                  <RotateCw size={14} strokeWidth={1.5} /> Restart
                </div>
              </button>
            </div>

            <div className="mt-12 text-center">
              <Link to="/" className="text-sm border-b border-current pb-0.5" data-testid="quiz-back-home">
                ← Back to home
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const q = QUIZ_QUESTIONS[step];
  const progress = ((step + 1) / total) * 100;

  return (
    <main className="min-h-[90vh]" data-testid="quiz-page">
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between mb-8">
            <div className="eyebrow" data-testid="quiz-progress">Question {step + 1} of {total}</div>
            {step > 0 && (
              <button onClick={back} className="text-sm inline-flex items-center gap-1 hover:opacity-70" data-testid="quiz-back">
                <ArrowLeft size={14} strokeWidth={1.5} /> Back
              </button>
            )}
          </div>

          <div className="h-px relative mb-12" style={{ background: "var(--sano-border)" }}>
            <div
              className="absolute left-0 top-0 h-px transition-all duration-500"
              style={{ width: `${progress}%`, background: "var(--sano-olive)" }}
            />
          </div>

          <h1
            key={step}
            className="font-serif-display text-4xl sm:text-5xl leading-tight mb-12 soft-up"
            data-testid="quiz-question"
          >
            {q.q}
          </h1>

          <div className="space-y-3">
            {q.a.map((opt, i) => (
              <button
                key={i}
                onClick={() => pick(opt.e)}
                className="w-full text-left p-6 border transition-all hover:border-[#3A5A40] hover:bg-[#F0EBE1] flex items-center justify-between group"
                style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}
                data-testid={`quiz-option-${step}-${i}`}
              >
                <span className="text-base leading-relaxed pr-6">{opt.t}</span>
                <ArrowRight
                  size={18}
                  strokeWidth={1.5}
                  className="shrink-0 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                  style={{ color: "var(--sano-olive)" }}
                />
              </button>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function Pillar({ title, items, testid }) {
  return (
    <div data-testid={`quiz-pillar-${testid}`}>
      <div className="eyebrow mb-4">{title}</div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it} className="font-serif-display text-xl">{it}</li>
        ))}
      </ul>
    </div>
  );
}
